import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import logo from '../assets/Dental-sleep-care-logo.png';

const { FiMenu, FiX, FiChevronDown } = FiIcons;

// 1. Restructured data to include subLinks for Sleep Apnea
const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About Us', path: '/about' }, // Assuming /about is still the main About Us page
  { name: 'Myofunctional Therapy', path: '/myofunctional-therapy' },
  {
    name: 'Sleep Apnea',
    subLinks: [
      { name: 'Adults', path: '/sleep-apnea-adults' },
      { name: 'Children', path: '/sleep-apnea-children' },
    ]
  },
  { name: 'TMJ Treatment', path: '/tmj-treatment' },
  { name: 'Contact Us', path: '/contact' },
];

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [mobileDropdownOpen, setMobileDropdownOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-white shadow-sm border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-24">

          {/* Logo Container - Added padding right to guarantee no overlap */}
          <Link to="/" className="flex-shrink-0 flex items-center pr-4">
            <img
              src={logo}
              alt="Dental Sleep Care"
              className="h-12 w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center xl:space-x-8 lg:space-x-5">
            <div className="flex xl:space-x-8 lg:space-x-4 h-full">
              {navLinks.map((link) => {
                // Dropdown logic for items with subLinks
                if (link.subLinks) {
                  return (
                    <div className="relative group flex items-center" key={link.name}>
                      <button className="flex items-center text-[15px] font-medium text-gray-600 hover:text-primary transition-colors py-2">
                        {link.name}
                        <FiChevronDown className="ml-1 w-4 h-4 transition-transform duration-200 group-hover:rotate-180 text-primary" />
                      </button>

                      {/* Dropdown Panel */}
                      <div className="absolute top-16 left-1/2 -translate-x-1/2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 ease-out transform translate-y-2 group-hover:translate-y-0 z-50">
                        <div className="py-2 px-2 flex flex-col space-y-1">
                          {link.subLinks.map((sub) => (
                            <NavLink
                              key={sub.path}
                              to={sub.path}
                              className={({ isActive }) =>
                                `block px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive
                                  ? 'bg-primary/10 text-primary'
                                  : 'text-gray-600 hover:bg-gray-50 hover:text-primary'
                                }`
                              }
                            >
                              {sub.name}
                            </NavLink>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Normal Links
                return (
                  <NavLink
                    key={link.path}
                    to={link.path}
                    className={({ isActive }) =>
                      `flex items-center text-[15px] font-medium transition-colors hover:text-primary ${isActive ? 'text-primary' : 'text-gray-600'
                      }`
                    }
                  >
                    {link.name}
                  </NavLink>
                );
              })}
            </div>

            {/* Prominent CTA Button */}
            <Link
              to="https://api.whatsapp.com/send?phone=917013686917&text=APPOINTMENT"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-primary hover:bg-accent2 text-white px-6 py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg text-lg"
            >
              Book Appointment
            </Link>
          </div>

          {/* Mobile Menu Hamburger Button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-600 hover:text-primary focus:outline-none p-2 bg-gray-50 rounded-lg"
            >
              <SafeIcon icon={isOpen ? FiX : FiMenu} className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Dropdown */}
      {isOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 absolute w-full left-0 shadow-2xl z-50">
          <div className="px-4 pt-3 pb-8 max-h-[85vh] overflow-y-auto space-y-2">
            {navLinks.map((link) => {
              // Mobile Dropdown Item
              if (link.subLinks) {
                return (
                  <div key={link.name} className="flex flex-col">
                    <button
                      onClick={() => setMobileDropdownOpen(!mobileDropdownOpen)}
                      className="flex items-center justify-between px-3 py-3.5 rounded-xl text-base font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      {link.name}
                      <FiChevronDown
                        className={`w-5 h-5 text-primary transition-transform duration-300 ${mobileDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    </button>
                    {/* Mobile Nested Links */}
                    <div className={`overflow-hidden transition-all duration-300 ${mobileDropdownOpen ? 'max-h-40 opacity-100 mt-1' : 'max-h-0 opacity-0'}`}>
                      <div className="px-4 py-2 space-y-1 bg-gray-50 rounded-xl mx-2 border border-gray-100">
                        {link.subLinks.map((sub) => (
                          <NavLink
                            key={sub.path}
                            to={sub.path}
                            onClick={() => setIsOpen(false)}
                            className={({ isActive }) =>
                              `block px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-gray-600 hover:text-primary'
                              }`
                            }
                          >
                            {sub.name}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              }

              // Mobile Standard Item
              return (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-3.5 rounded-xl text-base font-medium transition-colors ${isActive ? 'bg-primary/10 text-primary' : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                    }`
                  }
                >
                  {link.name}
                </NavLink>
              );
            })}

            {/* Mobile CTA Button */}
            <div className="pt-4 px-2">
              <Link
                to="https://api.whatsapp.com/send?phone=917013686917&text=APPOINTMENT"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-primary hover:bg-secondary text-white px-6 py-3 rounded-xl font-medium transition-all shadow-md hover:shadow-lg text-lg"
              >
                Book Appointment
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
